import { createSlice, createAction } from '@reduxjs/toolkit';
import { customerService } from '@virginmoney/api-connector-lib';
import { NTB_JOURNEY } from 'constants/general';
import { hasError } from '../utils/hasError';
import { updateConfig } from '../slices/configDetails';
import { retry } from '../utils/retry';
import { customerDataSelector } from '../selectors/customerDataSelector';
import { Analytics } from '../../../../utils/analytics';

function startLoading(state) {
  state.isLoading = true;
}

function loadingFailed(state, action) {
  state.isLoading = false;
  state.error = action.payload;
}

const initialState = {
  isLoading: false,
  isEditing: false,
  editPath: null,
  returnPath: null,
  error: null,
  info: {
    token: null,
    customerId: null
  },
  details: {},
  existingCustomerData: null,
  reviewSubmitted: false,
  hasDuplicateUser: false,
  hasResetForSecondaryApplicant: false,
  hasCYBDuplicateCustomer: false
};

export const api = createSlice({
  name: 'api',
  initialState,
  reducers: {
    getCustomerStart: startLoading,
    getCustomerFailed: loadingFailed,
    createCustomerStart: startLoading,
    createCustomerFailed: loadingFailed,
    updateCustomerStart: startLoading,
    updateCustomerFailed: loadingFailed,
    updateCustomerDetailsStart: startLoading,
    updateCustomerDetailsFailed: loadingFailed,

    startEditing: (state, action) => {
      state.isEditing = true;
      state.returnPath = window.location.pathname;
      state.editPath = action.payload;
    },
    stopLoading: (state) => {
      state.isLoading = false;
    },
    finishEditing: (state) => {
      state.isEditing = false;
      state.returnPath = null;
      state.editPath = null;
    },
    getCustomerSuccess: (state, action) => {
      state.existingCustomerData = action.payload;
      state.isLoading = false;
    },
    createCustomerSuccess: (state, action) => {
      const { access_token, id } = action.payload;
      state.info.token = access_token;
      state.info.customerId = id;
      state.isLoading = false;
      state.error = null;
    },
    updateCustomerSuccess: (state, action) => {
      state.isLoading = false;
      state.error = null;
      state.details = action.payload;
    },
    updateCustomerDetailsSuccess: (state) => {
      state.isLoading = false;
      state.error = null;
    },
    reviewSubmitted: (state, action) => {
      state.reviewSubmitted = action.payload;
    },
    setDuplicateUserStatus: (state, action) => {
      state.hasDuplicateUser = action.payload;
    },
    setDuplicateCustomerInCBYB: (state) => {
      state.hasCYBDuplicateCustomer = true;
    },
    resetForSecondaryApplicant: (state) => {
      state.hasResetForSecondaryApplicant = true;
    }
  }
});

/**
 * @todo this doesn't meet the Standard Actions spec. Should be payload: {}
 * this is also an action, ans should probably be in a different file
 */
const getCustomerSuccess = createAction('api/getCustomerSuccess', function prepare(payload) {
  return {
    payload
  };
});

export const {
  getCustomerStart,
  getCustomerFailed,
  createCustomerStart,
  createCustomerSuccess,
  createCustomerFailed,
  updateCustomerStart,
  updateCustomerSuccess,
  updateCustomerFailed,
  updateCustomerDetailsStart,
  updateCustomerDetailsSuccess,
  updateCustomerDetailsFailed,
  setDuplicateUserStatus,
  startEditing,
  stopLoading,
  finishEditing,
  reviewSubmitted,
  resetForSecondaryApplicant,
  setDuplicateCustomerInCBYB
} = api.actions;

/**
 * Thunk action which navigates the user back to the current Customer Information `returnPath` while
 * also providing and
 * @param {Function} goToPath A user-provided navigation callback that takes a string path. In loans, this is usually `navigateToScreen()`
 * @param {String[]} editablePaths
 * @returns
 */
export const stopEditing =
  (goToPath, editablePaths = []) =>
  (_dispatch, getState) => {
    /**
     * The current `window.location.path` at the time the `startEditing()` actionCreator is dispatched
     * will be used as the path to navigate the user to.
     * This is typically a "Review" screen of some kind.
     * @example Review -> Edit EmploymentDetails -> Review
     * @see startEditing
     */
    const { returnPath } = getState().api;

    goToPath(returnPath, {
      editablePaths
    });
  };

const getPersonalCustomer = async (options) => {
  const { paasApiBaseUrl, bankId, userTrackingId, token, customerId } = options;
  return await customerService.getCustomerById(
    paasApiBaseUrl,
    bankId,
    token,
    userTrackingId,
    customerId
  );
};

const updatePersonalCustomer = async (options) => {
  const { bankId, baseUrl, customerId, payload, token, userTrackingId } = options;
  const body = {
    applicants: payload.applicants.map((applicant) => {
      return {
        id: customerId,
        ...applicant
      };
    })
  };
  const response = await customerService.updatePersonalCustomer({
    bankId,
    baseUrl,
    body,
    customerId,
    token,
    userTrackingId
  });

  if (!hasError(response)) {
    return response;
  }
  updateCustInfoError(response);
};

const updatePersonalCustomerDetails = async (options, dispatch, globalErrorAction) => {
  const { bankId, baseUrl, customerId, payload, token, userTrackingId } = options;

  /**
   * NOTE: On the first request, this service will always (by design?) return 403.
   *
   * In order for this to succeed, we have to encapsulate it in a retry method,
   * and also delay it by 5 seconds 😤
   *
   */
  dispatch(updateCustomerDetailsStart());
  const retryResponse = await retry(
    async () => {
      dispatch(updateCustomerDetailsStart());
      return await customerService.updatePersonalCustomerPersonalDetails({
        bankId,
        baseUrl,
        body: {
          applicants: payload.applicants.map((applicant) => {
            const { contact_details, ...personal_details } = applicant.personal_details;
            return {
              id: customerId,
              personal_details,
              contact_details
            };
          })
        },
        customerId,
        token,
        userTrackingId
      });
    },
    6,
    3000
  );
  if (!hasError(retryResponse)) {
    return retryResponse;
  } else {
    dispatch(handleUpdateCustomerFailedResponse(retryResponse, globalErrorAction));
    return retryResponse;
  }
};
export const getCustomer = (options, globalErrorAction) => async (dispatch) => {
  try {
    dispatch(getCustomerStart());
    const response = await getPersonalCustomer(options);
    if (!hasError(response)) {
      await dispatch(getCustomerSuccess(response));
    }
  } catch (error) {
    dispatch(getCustomerFailed(error));
    globalErrorAction();
  }
};

const createPersonalCustomer = async (options, dispatch) => {
  const { paasApiBaseUrl, bankId, userTrackingId, payload, token } = options;

  const response = await customerService.createPersonalCustomer(
    paasApiBaseUrl,
    bankId,
    userTrackingId,
    payload,
    true
  );
  if (!hasError(response) && !token) {
    dispatch(
      updateConfig({
        token: response.access_token,
        customerId: response.id
      })
    );
  }
  updateCustInfoError(response);
  return response;
};
export const createCustomer = (options, globalErrorAction, callback) => async (dispatch) => {
  try {
    dispatch(createCustomerStart());
    const response = await createPersonalCustomer(options, dispatch);
    if (!hasError(response)) {
      dispatch(createCustomerSuccess(response));
      dispatch(tryUpdateCustomer(options, globalErrorAction, callback));
      dispatch(setDuplicateUserStatus(false));
    } else {
      const {
        response: { data }
      } = response;

      if (data) {
        const errorCode = data.error.code && parseInt(data.error.code);
        if (errorCode === 350) {
          dispatch(setDuplicateCustomerInCBYB());
          dispatch(stopLoading());
        } else if (errorCode >= 324 && errorCode <= 327) {
          dispatch(setDuplicateUserStatus(true));
          dispatch(stopLoading());
        } else {
          dispatch(createCustomerFailed(response.error));
          globalErrorAction();
        }
      } else {
        dispatch(createCustomerFailed(response.error));
        globalErrorAction();
      }
    }
  } catch (error) {
    dispatch(createCustomerFailed(error));
    globalErrorAction();
  }
};

export const tryUpdateCustomer = (options, globalErrorAction, callback) => {
  return async (dispatch, getState) => {
    const {
      bankId,
      paasApiBaseUrl: baseUrl,
      userTrackingId,
      isIdentityStatusChecked = false,
      isResume = false,
      payload: resumePayload
    } = options;
    const {
      config: { customerId, token, journey }
    } = getState();
    const payload =
      isResume && journey === NTB_JOURNEY ? resumePayload : customerDataSelector(getState());

    const custId = customerId || options.customerId;
    if (!isIdentityStatusChecked) {
      const response = await dispatch(
        updateCustomerDetails(
          {
            bankId,
            baseUrl,
            customerId: custId,
            token: token || options.token,
            userTrackingId,
            payload
          },
          globalErrorAction
        )
      );
      if (hasError(response)) return null;
    }
    const response = await dispatch(
      updateCustomer(
        {
          bankId,
          baseUrl,
          customerId: custId,
          payload,
          token: token || options.token,
          userTrackingId
        },
        globalErrorAction
      )
    );

    if (!hasError(response)) {
      callback(token, customerId);
    }
  };
};

export const updateCustomer = (options, globalErrorAction) => async (dispatch) => {
  try {
    dispatch(updateCustomerStart());
    const response = await updatePersonalCustomer(options);
    if (!hasError(response)) {
      dispatch(updateCustomerSuccess(response));
    }
  } catch (error) {
    dispatch(updateCustomerFailed(error));
    globalErrorAction();
  }
};

export const updateCustomerDetails = (options, globalErrorAction) => async (dispatch) => {
  try {
    dispatch(updateCustomerDetailsStart());
    const response = await updatePersonalCustomerDetails(options, dispatch, globalErrorAction);
    if (!hasError(response)) {
      dispatch(updateCustomerDetailsSuccess(response));
    }
    return response;
  } catch (error) {
    dispatch(updateCustomerDetailsFailed(error));
    globalErrorAction();
  }
};

const handleUpdateCustomerFailedResponse =
  ({ response, error }, globalErrorAction) =>
  (dispatch) => {
    const { data } = response;

    if (data) {
      const errorCode = data.error.code && parseInt(data.error.code);
      if (errorCode === 328) {
        dispatch(setDuplicateCustomerInCBYB());
        dispatch(stopLoading());
      } else {
        dispatch(updateCustomerDetailsFailed(error));
        globalErrorAction();
      }
      updateCustInfoError(response);
    } else {
      dispatch(updateCustomerDetailsFailed(error));
      globalErrorAction();
    }
  };

export const reviewScreenSubmitted = (isSubmitted) => async (dispatch) => {
  dispatch(reviewSubmitted(isSubmitted));
};

const updateCustInfoError = (response) => {
  if (hasError(response) && response.response && response.response.data) {
    const {
      response: { data }
    } = response;
    const errorCode = data.error.code || data.status;
    const errorMessage = data.error.message || data.error;
    Analytics.sendCustInfoError(errorCode, errorMessage);
  }
};
